import type { Asset } from '../types'

/**
 * Turning stored paths into links a browser can load
 * -------------------------------------------------------------
 * The images live in a private bucket now, so `library/KH98/front.png` is not
 * something an `<img>` can fetch. It has to be exchanged for a signed link,
 * and that exchange costs a request — which is why it happens once per page of
 * results rather than once per image.
 *
 * WHY BATCHED, AND NOT A REDIRECT PER IMAGE
 * The obvious alternative is an endpoint that redirects each image to a freshly
 * signed URL. It needs no batching and no change anywhere else, and it quietly
 * destroys browser caching: every page load produces a different target, so
 * every thumbnail is downloaded again. This library has already run a Supabase
 * account out of its egress allowance once. A signed link is a stable URL for
 * as long as it lives, so the browser caches it like any other image.
 */

/**
 * How long a signed link lives.
 *
 * Two hours is not a guess. Miro places images by handing its own servers the
 * URL and letting them fetch it, at roughly 1.2 images a second — a thousand
 * images is a quarter of an hour of fetching, and the panel may have been
 * opened a while before that. A link that expires mid-batch leaves half a board
 * filled and no useful error.
 *
 * The cost of the length: a page left open longer than this shows broken
 * images until it is reloaded, and a link that leaks stays usable until it
 * expires. Shorter is safer and breaks placement; this is the balance.
 */
export const SIGNED_URL_TTL_SECONDS = 2 * 60 * 60

/**
 * Values that are already loadable are left alone: mock data and the local
 * library folder hold real URLs, and an image built from a data: or blob: URL
 * has nothing to sign.
 */
export function needsSigning(path: string): boolean {
  return Boolean(path) && !/^(https?:|data:|blob:|\/)/i.test(path)
}

/** Every stored path an image list refers to, without duplicates. */
export function pathsToSign(assets: Asset[]): string[] {
  const paths = new Set<string>()
  for (const asset of assets) {
    if (needsSigning(asset.originalPath)) paths.add(asset.originalPath)
    if (needsSigning(asset.thumbnailPath)) paths.add(asset.thumbnailPath)
  }
  return [...paths]
}

/**
 * Put the signed links in place of the paths.
 *
 * A path that still needs signing and got no link is BLANKED. It used to be
 * left as it was, on the theory that a broken image is easier to spot than an
 * empty one. In practice (2026-09-30) the browser asked the website for
 * `child-safety/thumbs/xxx.webp`, the website answered with its own HTML page,
 * and the card gave up with "Untitled image" after three pointless requests.
 * A blank path lets the card move straight on to the other file it has.
 */
export function applySignedUrls(
  assets: Asset[],
  signed: Map<string, string>,
  /** Thumbnail links from the Worker (thumbLinks.ts); used for thumbnails only. */
  thumbs: Map<string, string> = new Map(),
): Asset[] {
  const pick = (path: string) => signed.get(path) ?? (needsSigning(path) ? '' : path)
  return assets.map((asset) => ({
    ...asset,
    originalPath: pick(asset.originalPath),
    thumbnailPath: thumbs.get(asset.thumbnailPath) ?? pick(asset.thumbnailPath),
  }))
}

/**
 * What one signing request gave back: links for the paths it could sign, and
 * the reason for any it could not.
 */
export interface SignAnswer {
  signed: Map<string, string>
  problems: Map<string, string>
}

/** Waits before the second and third tries. */
const SIGN_RETRY_MS = [600, 2000]

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Sign a page of images, trying again for whatever did not come back.
 *
 * Seen on 2026-09-30: a page of the library where a few cards, or nearly all,
 * stayed "Untitled image" until a reload, and the reload fixed them. Either
 * the whole signing request failed or some paths in it came back without a
 * link, and both were final: one try per page. Now the paths still missing
 * are asked for again, up to three tries in all, a little apart. Only the
 * missing ones, so a retry costs almost nothing.
 *
 * Whatever is still missing after that is said out loud with the reason
 * Storage gave, so the next time it is a fact rather than a guess.
 */
export async function signPatiently(
  assets: Asset[],
  signer: (paths: string[]) => Promise<SignAnswer>,
  /**
   * Thumbnails already given a link by the Worker (thumbLinks.ts). Those are
   * not signed again -- unless the same file is also a picture's full-size
   * one, which must stay a Supabase link for Miro.
   */
  thumbs: Map<string, string> = new Map(),
): Promise<Asset[]> {
  const originals = new Set(assets.map((asset) => asset.originalPath))
  const wanted = pathsToSign(assets).filter((path) => !thumbs.has(path) || originals.has(path))
  if (wanted.length === 0) return applySignedUrls(assets, new Map(), thumbs)

  const signed = new Map<string, string>()
  const problems = new Map<string, string>()
  let missing = wanted
  let lastFailure = ''

  for (let attempt = 0; attempt <= SIGN_RETRY_MS.length && missing.length > 0; attempt += 1) {
    if (attempt > 0) await pause(SIGN_RETRY_MS[attempt - 1])
    try {
      const answer = await signer(missing)
      for (const [path, url] of answer.signed) signed.set(path, url)
      for (const [path, why] of answer.problems) problems.set(path, why)
    } catch (error) {
      lastFailure = error instanceof Error ? error.message : String(error)
    }
    missing = missing.filter((path) => !signed.has(path))
  }

  if (missing.length > 0) {
    console.error(
      `[WonderWorks] could not sign ${missing.length} of ${wanted.length} image links`,
      missing.slice(0, 5).map((path) => `${path}: ${problems.get(path) ?? (lastFailure || 'no link returned')}`),
    )
  }
  return applySignedUrls(assets, signed, thumbs)
}
